const LWSAlgoliaSettings = window.LWSAlgoliaSettings || {};

window.LWSAlgolia = {
  enabled: LWSAlgoliaSettings.enabled ?? true,

  config: {
    appId: "WLYBF4QWV3",
    apiKey: "4c6513e0016e763da92b5a7d614a5f4a",
    indexName: "shopify_products",
    hitsPerPage: 24,
    collectionAttribute: "collection_ids",

    excludedFacets: [
      "collections",
      "collection_ids"
    ],

    fallbackFacetLabels: {
      vendor: "Marque",
      product_type: "Type de produit",
      price_range: "Prix",
      inventory_available: "Disponibilité"
    },
    sortIndexes: {
      "manual": "shopify_products",
      "best-selling": "shopify_products",

      "title-ascending": "shopify_products_title_asc",
      "title-descending": "shopify_products_title_desc",

      "price-ascending": "shopify_products_price_asc",
      "price-descending": "shopify_products_price_desc",

      "created-descending": "shopify_products_published_at_desc",
      "created-ascending": "shopify_products_published_at_asc"
    }
  },

  state: {
    client: null,
    lastResponse: null,
    selectedFilters: {},
    searchParams: "",
    facetsInitialized: false
  },

  init() {
    if (!window["algoliasearch/lite"]) {
      console.error("Algolia Lite client is not loaded.");
      this.enabled = false;
      return;
    }

    const { liteClient } = window["algoliasearch/lite"];
    this.state.client = liteClient(this.config.appId, this.config.apiKey);
  },

  shouldUseShopify() {
    return (
      LWSAlgoliaSettings.forceShopify === true ||
      LWSAlgoliaSettings.b2bCustomer === true ||
      new URLSearchParams(window.location.search).has("force_shopify")
    );
  },

  async render(searchParams = "", event = null, updateURLHash = true, appendResults = false) {
    this.state.searchParams = searchParams;
    this.filters.syncSelectedFiltersFromParams(new URLSearchParams(searchParams));

    const useShopify = this.shouldUseShopify();

    if (!useShopify) {
      if (!this.state.client) this.init();

      if (!this.state.client) {
        console.error("Algolia client not initialized.");
        return;
      }
    }

    try {
      this.renderer.setLoading(true);

      const response = useShopify
        ? window.LWSAlgoliaPageType === "search"
          ? await this.api.fetchShopifySearch(searchParams)
          : await this.api.fetchShopifyCollection(searchParams)
        : await this.api.search(searchParams);
      this.state.lastResponse = response;

      await this.renderer.renderProducts(response, appendResults);
      this.facets.update(response);
      this.renderer.renderCount(response);
      this.pagination.render(response);

      if (updateURLHash) {
        this.updateURLHash(searchParams);
      }
    } catch (error) {
      console.error("LWS Algolia error:", error);
    } finally {
      this.renderer.setLoading(false);
    }
  },

  api: {
    async search(searchParams = "") {
      const params = new URLSearchParams(searchParams);
      const page = Math.max(Number(params.get("page") || 1) - 1, 0);
      const filters = window.LWSAlgolia.filters.build(params);
      
      const sortBy = params.get("sort_by") || "manual";
      const sortIndexes = window.LWSAlgolia.config.sortIndexes || {};
      const indexName = sortIndexes[sortBy] || window.LWSAlgolia.config.indexName;

      const response = await window.LWSAlgolia.state.client.search([
        {
          indexName: indexName,
          params: {
            query: params.get("q") || "",
            page,
            hitsPerPage: window.LWSAlgolia.config.hitsPerPage,
            filters,
            facets: ["*"],
            maxValuesPerFacet: 100,
            attributesToRetrieve: [
              "id",
              "objectID",
              "title",
              "handle",
              "vendor",
              "product_type",
              "sku",
              "barcode",
              "image",
              "product_image",
              "price",
              "compare_at_price",
              "inventory_available",
              "inventory_quantity",
              "named_tags",
              "meta"
            ]
          }
        }
      ]);

      return response.results[0];
    },

    algoliaToShopifyFilterParam(facetName) {
      const map = {
        "vendor": "filter.p.vendor",
        "product_type": "filter.p.product_type",
        "inventory_available": "filter.v.availability"
      };
      if (map[facetName]) return map[facetName];
      const m = facetName.match(/^meta\.custom\.(.+)$/);
      if (m) return `filter.p.m.custom.${m[1]}`;
      return null;
    },

    shopifyParamToFacet(paramName) {
      const known = {
        "filter.p.vendor": "vendor",
        "filter.p.product_type": "product_type",
        "filter.v.availability": "inventory_available",
        "filter.v.price": "price"
      };
      if (known[paramName]) return known[paramName];
      const m = paramName.match(/^filter\.[pv]\.m\.custom\.(.+)$/);
      if (m) return `meta.custom.${m[1]}`;
      return paramName;
    },

    async fetchShopifyResults(searchParams = "", view = "products-json") {
      const params = new URLSearchParams(searchParams);
      const page = params.get("page") || "1";
      const sortBy = params.get("sort_by");
      const query = params.get("q");

      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set("view", view);
      if (view === "results-json") {
        url.searchParams.set("type", "product");
      }
      if (query) {
        url.searchParams.set("q", query);
      }
      if (sortBy) {
        url.searchParams.set("sort_by", sortBy);
      }
      url.searchParams.set("page", page);

      params.forEach((value, key) => {
        if (!key.startsWith("filter.a.")) return;
        const facetName = key.replace("filter.a.", "");
        const shopifyParam = this.algoliaToShopifyFilterParam(facetName);
        if (shopifyParam) {
          url.searchParams.append(shopifyParam, value);
        }
      });

      params.forEach((value, key) => {
        if (key.startsWith("filter.p.") || key.startsWith("filter.v.")) {
          url.searchParams.append(key, value);
        }
      });

      const priceMin = params.get("price_min");
      const priceMax = params.get("price_max");
      if (priceMin) url.searchParams.set("filter.v.price.gte", priceMin);
      if (priceMax) url.searchParams.set("filter.v.price.lte", priceMax);

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(`Shopify collection fetch failed: ${response.status}`);
      }

      const data = await response.json();

      const facets = {};
      let priceStats = null;
      const excludedFilterParams = [
        "filter.v.availability",
        "filter.p.product_type"
      ];
      (data.filters || []).forEach((filter) => {
        const paramName = filter.param_name || "";
        if (excludedFilterParams.includes(paramName)) return;

        if (filter.type === "price_range") {
          priceStats = {
            min: 0,
            max: Number.isFinite(Number(filter.range_max)) ? Number(filter.range_max) / 100 : 0
          };
          facets.price = { _range: true };
          return;
        }

        if (!filter.values || !filter.values.length) return;

        const facetName = this.shopifyParamToFacet(paramName);

        facets[facetName] = {};
        filter.values.forEach((v) => {
          facets[facetName][v.value] = v.count;
        });
      });

      return {
        hits: (data.products || []).map((product) => ({
          objectID: product.handle,
          handle: product.handle,
          title: product.title,
          vendor: product.vendor,
          product_image: product.image || null,
          image: product.image || null,
          meta: product.meta || {}
        })),
        nbHits: data.count || 0,
        page: Math.max((data.page || 1) - 1, 0),
        nbPages: data.pages || 0,
        facets: facets,
        facets_stats: priceStats ? { price: priceStats } : {}
      };
    },

    async fetchShopifyCollection(searchParams = "") {
      return this.fetchShopifyResults(searchParams, "products-json");
    },

    async fetchShopifySearch(searchParams = "") {
      return this.fetchShopifyResults(searchParams, "results-json");
    }
  },

  filters: {
    syncSelectedFiltersFromParams(params) {
      const selectedFilters = {};

      for (const [key, value] of params.entries()) {
        if (!key.startsWith("filter.a.")) continue;

        selectedFilters[key] = selectedFilters[key] || [];

        if (!selectedFilters[key].includes(value)) {
          selectedFilters[key].push(value);
        }
      }

      window.LWSAlgolia.state.selectedFilters = selectedFilters;
    },

    build(params) {
      const filters = [];

      const isSearchPage = window.LWSAlgoliaPageType === "search";

       const collectionId = isSearchPage
    ? null
    : window.LWSAlgoliaCollectionId
      || window.location.pathname
          .split("/collections/")[1]
          ?.split("/")[0];

      if (
          collectionId &&
          collectionId !== "all" &&
          window.LWSAlgolia.config.collectionAttribute
        ) {
          filters.push(
            `${window.LWSAlgolia.config.collectionAttribute}:"${this.escape(collectionId)}"`
          );
        }

      const groupedFilters = {};

      for (const [key, value] of params.entries()) {
        if (!key.startsWith("filter.a.")) continue;

        const attribute = key.replace("filter.a.", "");
        groupedFilters[attribute] = groupedFilters[attribute] || [];
        groupedFilters[attribute].push(value);
      }

      Object.entries(groupedFilters).forEach(([attribute, values]) => {
        const uniqueValues = [...new Set(values)];

        const group = uniqueValues
          .map((value) => `${attribute}:"${this.escape(value)}"`)
          .join(" OR ");

        filters.push(`(${group})`);
      });

      const priceMinRaw = params.get("price_min");
      const priceMaxRaw = params.get("price_max");

      const priceMin =
        priceMinRaw !== null && priceMinRaw !== ""
          ? Number(priceMinRaw)
          : null;

      const priceMax =
        priceMaxRaw !== null && priceMaxRaw !== ""
          ? Number(priceMaxRaw)
          : null;

      if (
        priceMin !== null &&
        priceMax !== null &&
        !Number.isNaN(priceMin) &&
        !Number.isNaN(priceMax)
      ) {
        filters.push(`price:${Math.min(priceMin, priceMax)} TO ${Math.max(priceMin, priceMax)}`);
      } else if (priceMin !== null && !Number.isNaN(priceMin)) {
        filters.push(`price >= ${priceMin}`);
      } else if (priceMax !== null && !Number.isNaN(priceMax)) {
        filters.push(`price <= ${priceMax}`);
      }

      return filters.join(" AND ");
    },

    buildParamsFromSelectedFilters() {
      const params = new URLSearchParams(window.LWSAlgolia.state.searchParams || window.location.search);

      Array.from(params.keys()).forEach((key) => {
        if (key.startsWith("filter.a.") || key === "page") {
          params.delete(key);
        }
      });

      Object.entries(window.LWSAlgolia.state.selectedFilters).forEach(([key, values]) => {
        values.forEach((value) => {
          params.append(key, value);
        });
      });

      return params;
    },

    escape(value) {
      return String(value).replaceAll("\\", "\\\\").replaceAll('"', '\\"');
    }
  },

  renderer: {
    setLoading(isLoading) {
      document
        .querySelector("#ProductGridContainer .collection")
        ?.classList.toggle("loading", isLoading);

      document
        .getElementById("ProductCount")
        ?.classList.toggle("loading", isLoading);
    },

    async renderProducts(response) {
      const activeView = document.querySelector(".collection-layout-view.is-active");

      const currentProducts =
        activeView?.querySelector("#product-grid") ||
        activeView?.querySelector("#product-rows") ||
        document.querySelector("#product-grid") ||
        document.querySelector("#product-rows");

      const isRow = !!activeView?.querySelector("#product-rows");

      const template = document.getElementById(
        isRow ? "AlgoliaProductRowTemplate" : "AlgoliaProductCardTemplate"
      );

      if (!currentProducts || !template) return;

      currentProducts.innerHTML = "";

      response.hits.forEach((hit) => {
        const clone = template.content.cloneNode(true);

        this.fillProductTemplate(clone, hit);

        currentProducts.appendChild(clone);
      });

      const savedLayout = localStorage.getItem("collection_products_layout");

      if (savedLayout && typeof window.setCollectionLayout === "function") {
        window.setCollectionLayout(savedLayout, false);
      }
    },

    fillProductTemplate(clone, hit) {
      const links = clone.querySelectorAll(".algolia-product-link");
      const image = clone.querySelector(".algolia-product-image");
      const vendor = clone.querySelector(".algolia-product-vendor");
      const sku = clone.querySelector(".algolia-product-sku");
      const title = clone.querySelector(".algolia-product-title");
      const metafieldsList = clone.querySelector(".algolia-product-metafields");
      if (metafieldsList) {
        this.renderMetafields(metafieldsList, hit);
      }

      const stockBadge = clone.querySelector(".card-product-stock");
      if (stockBadge) {
        const translations = window.LWSAlgoliaTranslations || {};
        const isInStock = Number(hit.inventory_quantity) > 0;

        stockBadge.className = isInStock
          ? "card-product-stock card-product-stock-in"
          : "card-product-stock card-product-stock-backorder";

        const textEl = stockBadge.querySelector(".card-product-stock-text");
        if (textEl) {
          textEl.textContent = isInStock
            ? translations.inStock || "En stock"
            : translations.backorder || "Disponible sur commande";
        }
      }

      const languageRoot = LWSAlgoliaSettings.languageRootUrl || "";
      const url = `${languageRoot === "/" ? "" : languageRoot}/products/${hit.handle}`;

      links.forEach((link) => {
        link.href = url;
      });

      if (title) title.textContent = hit.title || "";
      if (vendor) vendor.textContent = hit.vendor || "";
      const productSku = hit.sku || "";

      // if (sku) {
      //   sku.textContent = productSku;
      // }

      const imageUrl =
        hit.product_image ||
        hit.image ||
        image?.dataset.placeholderImage;

      if (image && imageUrl) {
        image.src = imageUrl;
        image.alt = hit.title || "";
      } else if (image) {
        image.closest(".card-media")?.remove();
      }

      const priceContainer = clone.querySelector(
        ".algolia-product-price-container"
      );

      if (priceContainer) {
        this.fillProductPrice(priceContainer, hit, productSku);
      }
    },

    async fillProductPrice(container, hit, sku) {
      const settings = window.LWSAlgoliaProductCardSettings || {};

      const loginForPrice =
        settings.loginForPriceEnabled === true &&
        settings.customerLoggedIn !== true;

      if (loginForPrice) {
        container.innerHTML = this.buildQuoteButton(settings, sku);
        return;
      }

      try {
        const response = await fetch(`/products/${hit.handle}.js`);

        if (!response.ok) {
          container.innerHTML = this.buildQuoteButton(settings, sku);
          return;
        }

        const product = await response.json();

        const price = Number(product.price) || 0;
        const compareAt = Number(product.compare_at_price) || 0;
        const variant = product.variants?.[0];

        if (price === 0) {
          container.innerHTML = this.buildQuoteButton(settings, sku);
          return;
        }

        container.innerHTML = this.buildPriceHtml(price, compareAt);

        if (settings.addToCartEnabled === true && variant) {
          container.insertAdjacentHTML(
            "beforeend",
            this.buildAddToCart(variant, settings)
          );
        }
      } catch (error) {
        container.innerHTML = this.buildQuoteButton(settings, sku);
      }
    },

    buildQuoteButton(settings, sku) {
      const quoteUrl = settings.quoteUrl || "#";
      const quoteText = settings.quoteText || "Demander une soumission";

      return `
        <div class="price">
          <div class="price-container">
            <a
              href="${quoteUrl}"
              data-sku="${sku || ""}"
              class="button button-secondary button-add-to-cart js-open-quote-popup"
              style="width:100%;"
            >
              <span class="icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="10" viewBox="0 0 13 10" fill="none">
                  <path d="M7.7197 1.28033C7.4268 0.987437 7.4268 0.512558 7.7197 0.219668C8.0126 -0.0732225 8.4874 -0.0732225 8.7803 0.219668L12.7803 4.2197C13.0732 4.5126 13.0732 4.9874 12.7803 5.2803L8.7803 9.2803C8.4874 9.5732 8.0126 9.5732 7.7197 9.2803C7.4268 8.9874 7.4268 8.5126 7.7197 8.2197L10.4393 5.5H0.75C0.33579 5.5 0 5.1642 0 4.75C0 4.3358 0.33579 4 0.75 4H10.4393L7.7197 1.28033Z" fill="white"></path>
                </svg>
              </span>
              ${quoteText}
            </a>
          </div>
        </div>
      `;
    },

    buildPriceHtml(price, compareAt) {
      const onSale = compareAt > price;

      return `
        <div class="price${onSale ? " price-on-sale" : ""}">
          <div class="price-container">
            <div class="price-regular">
              <span class="visually-hidden">Prix</span>
              <span class="price-item price-item-regular">${this.formatMoney(price)}</span>
            </div>
            ${
              onSale
                ? `
            <div class="price-sale">
              <s class="price-item price-item-regular">${this.formatMoney(compareAt)}</s>
              <span class="price-item price-item-sale price-item-last">${this.formatMoney(price)}</span>
            </div>
            `
                : ""
            }
          </div>
        </div>
      `;
    },

    formatMoney(cents) {
      const amount = cents / 100;

      return (
        "$" +
        amount.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      );
    },

    buildAddToCart(variant, settings) {
      const style = settings.addToCartStyle || "secondary";

      const label = variant.available
        ? settings.addToCartText || "Ajouter au panier"
        : settings.outOfStockText || "Rupture de stock";

      return `
        <product-form>
          <form
            method="post"
            action="${window.routes.cart_add_url}"
            data-type="add-to-cart-form"
            novalidate="novalidate"
          >
            <input type="hidden" name="id" value="${variant.id}">
            <input type="hidden" name="quantity" value="1">
            <div class="product-actions card-product-actions">
              <button
                type="submit"
                name="add"
                class="button button-${style} button-add-to-cart"
                ${variant.available ? "" : "disabled"}
              >
                <span>${label}</span>
                <div class="button-overlay-spinner hidden">
                  <span class="spinner-xs"></span>
                </div>
              </button>
            </div>
            <div
              class="product-form-error-message-wrapper form-status form-status-error"
              role="alert"
              hidden
            >
              <span class="product-form-error-message"></span>
            </div>
          </form>
        </product-form>
      `;
    },

    renderCount(response) {
      const count = document.getElementById("ProductCount");
      if (!count) return;

      const translations = window.LWSAlgoliaTranslations || {};

      count.textContent = (translations.productsCount || "[count] produits").replace("[count]", response.nbHits || 0);
      count.classList.remove("loading");
    },
    
    renderMetafields(container, hit) {
      container.innerHTML = "";

      const translations = window.LWSAlgoliaTranslations || {};

      const fields = [
        {
          label: `${translations.puissance || "Puissance"} (%{custom.unite_de_puissance})`,
          path: "custom.puissance"
        },
        {
          label: translations.vitesse || "Vitesse",
          path: "custom.vitesse"
        },
        {
          label: translations.voltage || "Voltage",
          path: "custom.voltage"
        },
        {
          label: translations.phase || "Phase",
          path: "custom.phase"
        },
        {
          label: translations.frame || "Bati",
          path: "custom.frame"
        },
        {
          label: translations.enclosure || "Boitier",
          path: "custom.enclosure"
        },
        {
          label: translations.torque || "Couple",
          path: "custom.torque"
        },
        {
          label: translations.gearRatio || "Ratio",
          path: "custom.gear_ratio"
        }
      ];

      fields.forEach((field) => {
        const value = this.getAlgoliaMetafieldValue(hit, field.path);

        if (!value) return;

        const label = this.replaceMetafieldPlaceholders(field.label, hit);

        const li = document.createElement("li");
        li.className = "product-info-details-item";

        li.innerHTML = `
          <span class="product-info-details-item-label">${label}</span>
          <span class="product-info-details-item-value">${value}</span>
        `;

        container.appendChild(li);
      });

      if (!container.children.length) {
        container.remove();
      }
    },

    replaceMetafieldPlaceholders(label, hit) {
      return label.replace(/%\{([^}]+)\}/g, (_, path) => {
        return this.getAlgoliaMetafieldValue(hit, path) || "";
      });
    },

    getAlgoliaMetafieldValue(hit, path) {
      const parts = path.split(".");
      let value = hit.meta;

      parts.forEach((part) => {
        value = value?.[part];
      });

      if (value == null || value === "") return "";

      if (Array.isArray(value)) {
        return value.join(", ");
      }

      if (typeof value === "object") {
        return value.value || value.label || "";
      }

      return String(value);
    }
  },

  facets: {
    update(response) {
      const container = document.querySelector(
        "#FacetFiltersForm .page-layout-sidebar-inner-content"
      );

      if (!container || !response.facets) return;

      if (!window.LWSAlgolia.state.facetsInitialized) {
        this.init(response);
        window.LWSAlgolia.state.facetsInitialized = true;
      }

      this.updateStates(response);
    },

    init(response) {
      const container = document.querySelector(
        "#FacetFiltersForm .page-layout-sidebar-inner-content"
      );

      if (!container || !response.facets) return;

      container
        .querySelectorAll('[data-algolia-filter="true"]')
        .forEach((filter) => filter.remove());

      Object.entries(response.facets).forEach(([facetName, values], index) => {
      if (window.LWSAlgolia.config.excludedFacets.includes(facetName)) return;
      if (!values || !Object.keys(values).length) return;

      const facetElement =
        facetName === "price"
          ? this.createPriceFacet(response, index + 1)
          : this.createFacetElement(facetName, values, index + 1);

      container.appendChild(facetElement);
    });
    },

    createPriceFacet(response, index) {
      const translations = window.LWSAlgoliaTranslations || {};
      const wrapper = document.createElement("div");
      wrapper.className = "js-filter";
      wrapper.dataset.algoliaFilter = "true";
      wrapper.dataset.index = index;
      wrapper.dataset.name = "price";
      wrapper.dataset.facetName = "price";

      const outer = document.createElement("div");

      const collapsible = document.createElement("collapsible-expandable");
      collapsible.setAttribute("expanded", "true");

      const facet = document.createElement("div");
      facet.className = "facet facet-price";
      facet.dataset.index = index;

      const toggle = document.createElement("button");
      toggle.className = "facet-toggle";
      toggle.type = "button";
      toggle.setAttribute("aria-expanded", "true");

      const title = document.createElement("span");
      title.textContent = this.getFacetLabel("price");

      toggle.appendChild(title);

      const content = document.createElement("div");
      content.className = "facet-content";

      const fields = document.createElement("div");
      fields.className = "facets-price-range";

      const params = new URLSearchParams(
        window.LWSAlgolia.state.searchParams || window.location.search
      );

      const stats = response.facets_stats?.price || {};

      const availableMin = Number.isFinite(Number(stats.min))
        ? Number(stats.min)
        : 0;

      const availableMax = Number.isFinite(Number(stats.max))
        ? Number(stats.max)
        : 0;

      const selectedMin = params.get("price_min") ?? "";
      const selectedMax = params.get("price_max") ?? "";

      const minField = this.createPriceField({
        id: "algolia-price-min",
        name: "price_min",
        label: translations.priceMin || "Prix minimum",
        placeholder: availableMin,
        value: selectedMin,
        min: availableMin,
        max: availableMax
      });

      const maxField = this.createPriceField({
        id: "algolia-price-max",
        name: "price_max",
        label: translations.priceMax || "Prix maximum",
        placeholder: availableMax,
        value: selectedMax,
        min: availableMin,
        max: availableMax
      });

      fields.appendChild(minField);
      fields.appendChild(maxField);

      const footer = document.createElement("div");
      footer.className = "facet-content-footer";

      const applyButton = document.createElement("button");
      applyButton.type = "button";
      applyButton.className = "button button-secondary algolia-price-apply";
      applyButton.textContent = translations.apply || "Appliquer";

      const resetButton = document.createElement("button");
      resetButton.type = "button";
      resetButton.className = "button-text-link algolia-price-reset";
      resetButton.textContent = translations.reset || "Réinitialiser";

      applyButton.addEventListener("click", () => {
        this.applyPriceFilter(wrapper);
      });

      resetButton.addEventListener("click", () => {
        const minInput = wrapper.querySelector('[name="price_min"]');
        const maxInput = wrapper.querySelector('[name="price_max"]');

        if (minInput) minInput.value = "";
        if (maxInput) maxInput.value = "";

        this.applyPriceFilter(wrapper);
      });

      footer.appendChild(applyButton);
      footer.appendChild(resetButton);

      content.appendChild(fields);
      content.appendChild(footer);

      facet.appendChild(toggle);
      facet.appendChild(content);

      collapsible.appendChild(facet);
      outer.appendChild(collapsible);
      wrapper.appendChild(outer);

      return wrapper;
    },

    createPriceField({
      id,
      name,
      label,
      placeholder,
      value,
      min,
      max
    }) {
      const field = document.createElement("div");
      field.className = "facet-price-range-field";

      const currency = document.createElement("span");
      currency.className = "facet-price-range-currency";
      currency.textContent = "$";

      const inputLabel = document.createElement("label");
      inputLabel.className = "visually-hidden";
      inputLabel.setAttribute("for", id);
      inputLabel.textContent = label;

      const input = document.createElement("input");
      input.type = "number";
      input.id = id;
      input.name = name;
      input.value = value;
      input.placeholder = Number.isFinite(Number(placeholder))
        ? String(placeholder)
        : "";

      input.min = Number.isFinite(Number(min))
        ? String(min)
        : "0";

      if (Number.isFinite(Number(max)) && Number(max) > 0) {
        input.max = String(max);
      }

      input.step = "0.01";
      input.inputMode = "decimal";

      input.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") return;

        event.preventDefault();

        const wrapper = input.closest('[data-facet-name="price"]');

        if (wrapper) {
          this.applyPriceFilter(wrapper);
        }
      });

      field.appendChild(currency);
      field.appendChild(inputLabel);
      field.appendChild(input);

      return field;
    },

    applyPriceFilter(wrapper) {
      const minInput = wrapper.querySelector('[name="price_min"]');
      const maxInput = wrapper.querySelector('[name="price_max"]');

      const params = new URLSearchParams(
        window.LWSAlgolia.state.searchParams || window.location.search
      );

      params.delete("page");

      const minValue = minInput?.value.trim() || "";
      const maxValue = maxInput?.value.trim() || "";

      if (minValue) {
        params.set("price_min", minValue);
      } else {
        params.delete("price_min");
      }

      if (maxValue) {
        params.set("price_max", maxValue);
      } else {
        params.delete("price_max");
      }

      window.LWSAlgolia.render(
        params.toString(),
        null,
        true,
        false
      );
    },

    createFacetElement(facetName, values, index) {
      const inputName = `filter.a.${facetName}`;
      const label = this.getFacetLabel(facetName);

      const wrapper = document.createElement("div");
      wrapper.className = "js-filter";
      wrapper.dataset.algoliaFilter = "true";
      wrapper.dataset.index = index;
      wrapper.dataset.name = inputName;
      wrapper.dataset.facetName = facetName;

      const outer = document.createElement("div");
      const collapsible = document.createElement("collapsible-expandable");
      collapsible.setAttribute("expanded", "true");

      const facet = document.createElement("div");
      facet.className = `facet ${Object.keys(values).length > 10 ? "facet-long" : ""}`;
      facet.dataset.index = index;

      const button = document.createElement("button");
      button.className = "facet-toggle";
      button.type = "button";
      button.setAttribute("aria-expanded", "true");

      const labelSpan = document.createElement("span");
      labelSpan.textContent = label;
      button.appendChild(labelSpan);

      const content = document.createElement("div");
      content.className = "facet-content";

      const list = document.createElement("ul");
      list.className = "facet-options-list";
      list.setAttribute("aria-expanded", "false");

      Object.entries(values)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 100)
      .forEach(([value, count], optionIndex) => {
        const option = this.createOptionElement({
          facetName,
          inputName,
          value,
          count,
          index,
          optionIndex
        });

        if (optionIndex >= 10) {
          option.hidden = true;
          option.dataset.facetExtraOption = "true";
        }

        list.appendChild(option);
      });

      content.appendChild(list);
      if (Object.keys(values).length > 10) {
        const footer = document.createElement("div");
        footer.className = "facet-content-footer";

        const button = document.createElement("button");
        button.className = "button-text-link facet-button-more";
        button.type = "button";
        button.setAttribute("form", "FacetsFilterForm");
        const translations = window.LWSAlgoliaTranslations || {};
        button.textContent = `+ ${translations.showMore || "Voir plus"}`;

        button.addEventListener("click", () => {
          const isExpanded = list.getAttribute("aria-expanded") === "true";

          list.setAttribute("aria-expanded", isExpanded ? "false" : "true");

          this.applyOptionsLimit(wrapper);
        });

        footer.appendChild(button);
        content.appendChild(footer);
      }
      facet.appendChild(button);
      facet.appendChild(content);
      collapsible.appendChild(facet);
      outer.appendChild(collapsible);
      wrapper.appendChild(outer);

      return wrapper;
    },

    createOptionElement({ facetName, inputName, value, count, index, optionIndex }) {
      const id = `algolia-filter-${index}-${optionIndex}`;

      const li = document.createElement("li");
      li.className = "facet-options-list-item";
      li.dataset.algoliaFacetOption = "true";
      li.dataset.facetName = facetName;
      li.dataset.facetValue = value;

      const span = document.createElement("span");
      span.className = "checkbox-facet";

      const label = document.createElement("label");
      label.className = "checkbox-facet-label";
      label.setAttribute("for", id);

      const input = document.createElement("input");
      input.type = "checkbox";
      input.id = id;
      input.name = inputName;
      input.value = value;
      input.setAttribute("form", "FacetsFilterForm");
      input.setAttribute("aria-label", value);

      const check = document.createElement("span");
      check.className = "checkbox-facet-check";

      const text = document.createElement("span");
      text.className = "checkbox-facet-label-text";
      text.textContent = this.getOptionLabel(facetName, value);

      const countEl = document.createElement("span");
      countEl.className = "checkbox-facet-label-count";
      countEl.textContent = count;

      label.appendChild(input);
      label.appendChild(check);
      label.appendChild(text);
      label.appendChild(countEl);

      span.appendChild(label);
      li.appendChild(span);

      return li;
    },

    updateStates(response) {
      const selectedFilters = window.LWSAlgolia.state.selectedFilters;

      Object.entries(response.facets || {}).forEach(([facetName, values]) => {
        if (window.LWSAlgolia.config.excludedFacets.includes(facetName)) return;

        const inputName = `filter.a.${facetName}`;
        const activeValues = selectedFilters[inputName] || [];
        const currentValues = values || {};

        const facetWrapper = document.querySelector(
          `[data-algolia-filter="true"][data-facet-name="${CSS.escape(facetName)}"]`
        );

        if (!facetWrapper) return;

        facetWrapper
          .querySelectorAll("[data-algolia-facet-option='true']")
          .forEach((option) => {
            const value = option.dataset.facetValue;
            const input = option.querySelector("input");
            const countEl = option.querySelector(".checkbox-facet-label-count");
            const checkbox = option.querySelector(".checkbox-facet");

            const isActive = activeValues.includes(value);
            const count = currentValues[value] ?? 0;

            input.checked = isActive;
            input.disabled = false;

            if (countEl) countEl.textContent = count;
            if (checkbox) checkbox.classList.remove("checkbox-facet-disabled");

            const isExtraOption = option.dataset.facetExtraOption === "true";
            const isExpanded =
              facetWrapper.querySelector(".facet-options-list")?.getAttribute("aria-expanded") === "true";

            option.hidden = isExtraOption && !isExpanded;
          });
        this.applyOptionsLimit(facetWrapper);
      });
    },
    applyOptionsLimit(facetWrapper) {
      const translations = window.LWSAlgoliaTranslations || {};
      const list = facetWrapper.querySelector(".facet-options-list");
      const button = facetWrapper.querySelector(".facet-button-more");
      const options = facetWrapper.querySelectorAll("[data-algolia-facet-option='true']");

      if (!list || !options.length) return;

      const isExpanded = list.getAttribute("aria-expanded") === "true";

      options.forEach((option, index) => {
        option.hidden = index >= 10 && !isExpanded;
      });

      if (button) {
        button.hidden = options.length <= 10;
        button.textContent = isExpanded
          ? `− ${translations.showLess || "Voir moins"}`
          : `+ ${translations.showMore || "Voir plus"}`;
      }
    },

    getFacetLabel(facetName) {
      const translations = window.LWSAlgoliaTranslations || {};

      const labels = {
        vendor: translations.brand || "Marque",
        product_type: translations.productType || "Type de produit",
        price: translations.price || "Prix",
        inventory_available: translations.availability || "Disponibilité",

        "meta.custom.frame": translations.frame || "Bati",
        "meta.custom.enclosure": translations.enclosure || "Boîtier",
        "meta.custom.puissance": translations.puissance || "Puissance",
        "meta.custom.vitesse": translations.vitesse || "Vitesse",
        "meta.custom.voltage": translations.voltage || "Voltage",
        "meta.custom.phase": translations.phase || "Phase",
        "meta.custom.horsepower": "Horsepower",
        "meta.custom.rpm": "RPM",
        "meta.custom.gear_ratio": translations.gearRatio || "Ratio",
        "meta.custom.torque": translations.torque || "Couple",
        "meta.custom.unite_de_puissance": translations.uniteDePuissance || "Unité de puissance",
        "meta.custom.vitesse_standardisee": translations.vitesseStandardisee || "Vitesse standardisée"
      };

      if (labels[facetName]) return labels[facetName];

      return facetName
        .replace(/^meta\.custom\./, "")
        .replace(/^named_tags\./, "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },

    getOptionLabel(facetName, value) {
      const translations = window.LWSAlgoliaTranslations || {};

      if (facetName === "inventory_available") {
        return value === "true"
          ? translations.inStock || "En stock"
          : translations.outOfStock || "Rupture de stock";
      }

      return String(value).replace(/\.0$/, "");
    }
  },

  pagination: {
    render(response) {
      const pagination = document.querySelector(".pagination");
      if (!pagination) return;

      const totalPages = response.nbPages || 0;
      const currentPage = (response.page || 0) + 1;

      pagination.innerHTML = "";
      pagination.dataset.page = currentPage;

      if (totalPages <= 1) {
        pagination.style.display = "none";
        return;
      }

      pagination.style.display = "";

      const addLink = (page, label = page, className = "") => {
        const link = document.createElement("a");
        link.href = this.pageUrl(page);
        link.className = `page-number ${className}`.trim();
        link.dataset.algoliaPage = page;
        link.setAttribute("aria-label", `Page ${page}`);
        link.textContent = label;
        pagination.appendChild(link);
      };

      const addSpan = (label, className = "") => {
        const span = document.createElement("span");
        span.className = `page-number ${className}`.trim();
        span.textContent = label;
        pagination.appendChild(span);
      };

      if (currentPage > 1) addLink(currentPage - 1, "‹", "page-number-prev");

      if (currentPage === 1) addSpan(1, "page-number-active");
      else addLink(1);

      const start = Math.max(2, currentPage - 2);
      const end = Math.min(totalPages - 1, currentPage + 2);

      if (start > 2) addSpan("…");

      for (let page = start; page <= end; page++) {
        if (page === currentPage) addSpan(page, "page-number-active");
        else addLink(page);
      }

      if (end < totalPages - 1) addSpan("…");

      if (totalPages > 1) {
        if (currentPage === totalPages) addSpan(totalPages, "page-number-active");
        else addLink(totalPages);
      }

      if (currentPage < totalPages) addLink(currentPage + 1, "›", "page-number-next");
    },

    pageUrl(page) {
      const params = new URLSearchParams(
        window.LWSAlgolia.state.searchParams || window.location.search
      );
      params.set("page", page);
      return `${window.location.pathname}?${params.toString()}`;
    }
  },

  updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams ? `?${searchParams}` : ""}`
    );
  }
};

document.addEventListener("DOMContentLoaded", () => {
  window.LWSAlgolia.init();

  if (window.LWSAlgoliaPageType === "search") return;

  if (!document.getElementById("ProductGridContainer")) return;

  window.LWSAlgolia.render(window.location.search.slice(1));
});

document.addEventListener("click", (event) => {
  const button = event.target.closest("[data-algolia-page]");
  if (!button) return;

  event.preventDefault();

  const params = new URLSearchParams(
    window.LWSAlgolia.state.searchParams || window.location.search
  );
  params.set("page", button.dataset.algoliaPage);

  window.LWSAlgolia.render(params.toString(), event, true, false);
  document.getElementById("MainContent")?.scrollIntoView(true);
});

document.addEventListener("change", (event) => {
  if (event.target.closest('[name="sort_by"]')) return;

  const input = event.target.closest('[data-algolia-filter="true"] input');
  if (!input) return;
  if (input.name === "price_min" || input.name === "price_max") {return;}

  event.preventDefault();
  event.stopPropagation();

  const selectedFilters = window.LWSAlgolia.state.selectedFilters;
  selectedFilters[input.name] = selectedFilters[input.name] || [];

  if (input.checked) {
    if (!selectedFilters[input.name].includes(input.value)) {
      selectedFilters[input.name].push(input.value);
    }
  } else {
    selectedFilters[input.name] = selectedFilters[input.name].filter(
      (value) => value !== input.value
    );

    if (!selectedFilters[input.name].length) {
      delete selectedFilters[input.name];
    }
  }

  const params = window.LWSAlgolia.filters.buildParamsFromSelectedFilters();
  window.LWSAlgolia.render(params.toString(), event, true, false);
});

document.addEventListener("change", (event) => {
  const sortSelect = event.target.closest('[name="sort_by"]');
  if (!sortSelect) return;

  event.preventDefault();
  event.stopPropagation();

  const params = new URLSearchParams(window.LWSAlgolia.state.searchParams || window.location.search);

  params.set("sort_by", sortSelect.value || "manual");
  params.delete("page");

  window.LWSAlgolia.render(params.toString(), event, true, false);
});